<template>
  <div
    v-if="items.length || loading"
    class="ui-gallery"
  >
    <div class="ui-gallery__header">
      <div class="ui-gallery__title">
        {{ name }}
      </div>
      <div
        class="ui-gallery__controls"
        :class="controlsClasses"
      >
        <UIButtonIcon
          v-bind="btnAttrs"
          :leading="ChevronDoubleLeftIcon"
          :disabled="arrivedState.left && !arrivedState.right"
          @click="handleScrollLeft"
        />
        <UIButtonIcon
          v-bind="btnAttrs"
          :leading="ArrowLeftIcon"
          :disabled="arrivedState.left && !arrivedState.right"
          @click="handleScrollStepLeft"
        />
        <UIButtonIcon
          v-bind="btnAttrs"
          :leading="ArrowRightIcon"
          :disabled="arrivedState.right && !arrivedState.left"
          @click="handleScrollStepRight"
        />
        <UIButtonIcon
          v-bind="btnAttrs"
          :leading="ChevronDoubleRightIcon"
          :disabled="arrivedState.right && !arrivedState.left"
          @click="handleScrollRight"
        />
      </div>
    </div>
    <div
      v-if="loading"
      class="ui-gallery__loading"
    >
      <slot name="loading" />
    </div>
    <div
      v-else
      class="ui-gallery__body-wrapper"
      :class="bodyClasses"
    >
      <div
        ref="bodyRef"
        class="ui-gallery__body no-scrollbar"
      >
        <slot
          v-for="(item, index) in outputItems"
          :key="keyField ? item[keyField as keyof T] : index"
          :item
          name="item"
        />
        <div
          v-if="isLazyIndicatorVisible"
          ref="endRef"
          class="ui-gallery__end"
        >
          <UIPreloader />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, onBeforeMount, ref, useTemplateRef, watch } from 'vue'
import { useElementVisibility, useResizeObserver, useScroll } from '@vueuse/core'
import { isEqual } from 'lodash'

import { useResponsive } from '@/plugins/responsiveUI'

import { ButtonSize } from '@types'

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/vue/20/solid'
import { UIButtonIcon } from '@ui/buttons'
import { UIPreloader } from '@ui/core'

type Props = {
  name?: string
  loading?: boolean
  items?: T[]
  keyField?: keyof T
  scrollOffset?: number
  lazy?: number
}

const { items = [], lazy = 0, scrollOffset = 200 } = defineProps<Props>()

const { isMobile } = useResponsive()

const bodyRef = useTemplateRef('bodyRef')
const endRef = useTemplateRef('endRef')

const btnAttrs = {
  variant: 'gray',
  fill: 'light',
  size: 'xs' as ButtonSize,
}

const isControlsVisible = ref(false)

const itemsPerPage = ref(0)

useResizeObserver(bodyRef, entries => {
  const { width } = entries[0].contentRect
  isControlsVisible.value = !!bodyRef.value && (Math.round(width) < Math.round(bodyRef.value?.scrollWidth))
})

const { arrivedState } = useScroll(bodyRef, {
  offset: { right: scrollOffset, left: scrollOffset },
})

const isScrolledRight = useElementVisibility(endRef)

const isLazyIndicatorVisible = computed(
  () => lazy && itemsPerPage.value < items.length,
)

const controlsClasses = computed(() => ({
  'isHidden': !isControlsVisible.value,
}))

const bodyClasses = computed(() => ({
  'hasLeftShadow': !arrivedState.left && !isMobile.value,
  'hasRightShadow': !arrivedState.right && !isMobile.value,
}))

const outputItems = computed<T[]>(oldValue => {
  let result
  if (itemsPerPage.value) {
    result = items.slice(0, itemsPerPage.value)
  } else {
    result = items
  }
  if (isEqual(oldValue, result)) {
    return oldValue || []
  } else {
    return result
  }
})

const handleScroll = (left: number) => {
  bodyRef.value?.scrollTo({
    left,
    behavior: 'smooth',
  })
}

const handleScrollLeft = () => {
  handleScroll(0)
}

const handleScrollRight = () => {
  if (!bodyRef.value) return
  handleScroll(bodyRef.value.scrollWidth)
}

const handleScrollStepLeft = () => {
  if (!bodyRef.value) return
  handleScroll(bodyRef.value.scrollLeft - scrollOffset * 2)
}

const handleScrollStepRight = () => {
  if (!bodyRef.value) return
  handleScroll(bodyRef.value.scrollLeft + scrollOffset * 2)
}

watch(isScrolledRight, (value, prev) => {
  if (!lazy || prev || !value) return
  itemsPerPage.value += lazy
})

onBeforeMount(() => {
  itemsPerPage.value = lazy
})
</script>

<style>
.ui-gallery {
  @apply flex flex-col;
  @apply gap-3;

  &__header {
    @apply flex justify-between items-center;
  }

  &__title {
    @apply text-lg sm:text-base;
    @apply leading-none;
    @apply text-common-text dark:text-gray-300;
  }

  &__controls {
    @apply flex items-center;
    @apply gap-1;

    &.isHidden {
      @apply hidden;
    }
  }

  &__control {

    &.isDisabled {
      @apply text-gray-300 dark:text-gray-500;
    }

    &:not(.isDisabled) {
      @apply text-gray-500 hover:text-indigo-700;
      @apply dark:text-gray-400 dark:hover:text-indigo-300;
      @apply cursor-pointer;
    }
  }

  &__loading,
  &__body {
    @apply flex;
    @apply gap-2;
  }

  &__body {
    @apply overflow-x-scroll overflow-y-hidden;

    & > * {
      scroll-snap-align: start;
    }
  }

  &__body-wrapper {
    @apply relative;

    &.hasLeftShadow {
      @apply before:content-[''] before:block before:h-full before:w-8 before:bg-linear-to-r before:from-card before:to-transparent;
      @apply before:absolute before:left-0 before:inset-y-0;
    }
    &.hasRightShadow {
      @apply after:content-[''] after:block after:h-full after:w-8 after:bg-linear-to-r after:from-transparent after:to-card;
      @apply after:absolute after:right-0 after:inset-y-0;
    }
  }

  &__end {
    @apply w-10;
    @apply flex justify-center items-center shrink-0;
  }
}
</style>
